import { useState } from 'react'
import { Activity, AlertCircle, CheckCircle, BarChart3, Clock, Zap, TrendingUp } from 'lucide-react'
import { useParams } from 'react-router-dom'

const botConfigs = {
  'joe-bot': {
    name: '🤖 Joe Bot',
    type: 'General Assistant',
    status: 'operational',
    uptime: 99.8,
    activeUsers: 245,
    requestsPerHour: 3847,
    avgResponseTime: 234,
  },
  'recruiter-bot': {
    name: '👔 Recruiter Bot',
    type: 'Recruitment Assistant',
    status: 'operational',
    uptime: 99.9,
    activeUsers: 89,
    requestsPerHour: 567,
    avgResponseTime: 156,
  },
  'onboard-bot': {
    name: '📋 Onboard Bot',
    type: 'Onboarding Assistant',
    status: 'operational',
    uptime: 99.7,
    activeUsers: 67,
    requestsPerHour: 342,
    avgResponseTime: 189,
  },
  'email-bot': {
    name: '✉️ Email Bot',
    type: 'Email Management',
    status: 'operational',
    uptime: 99.9,
    activeUsers: 156,
    requestsPerHour: 2134,
    avgResponseTime: 145,
  },
  'media-bot': {
    name: '🖼️ Media Bot',
    type: 'Media Processing',
    status: 'operational',
    uptime: 99.6,
    activeUsers: 112,
    requestsPerHour: 876,
    avgResponseTime: 432,
  },
  'ceo-joe-bot': {
    name: '👨‍💼 CEO Joe Bot',
    type: 'Executive Assistant',
    status: 'operational',
    uptime: 100,
    activeUsers: 1,
    requestsPerHour: 45,
    avgResponseTime: 234,
  },
  'meeting-center': {
    name: '📞 Meeting Center',
    type: 'Meeting Management',
    status: 'operational',
    uptime: 99.95,
    activeUsers: 340,
    requestsPerHour: 5234,
    avgResponseTime: 89,
  },
}

export default function BotMonitorPage() {
  const { botId } = useParams()
  const config = botConfigs[botId] || botConfigs['joe-bot']
  const [timeRange, setTimeRange] = useState('24h')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{config.name} Monitor</h1>
          <p className="text-blue-200 mt-1">{config.type} • Real-time monitoring and analytics</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Status</p>
            <Activity className="text-green-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white capitalize">{config.status}</p>
          <p className="text-xs text-green-600 mt-2">✓ All systems operational</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Uptime</p>
            <CheckCircle className="text-green-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{config.uptime}%</p>
          <p className="text-xs text-green-600 mt-2">Last 30 days</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Active Users</p>
            <TrendingUp className="text-blue-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{config.activeUsers}</p>
          <p className="text-xs text-blue-600 mt-2">Currently connected</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Avg Response</p>
            <Clock className="text-orange-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{config.avgResponseTime}ms</p>
          <p className="text-xs text-orange-600 mt-2">Per request</p>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Requests */}
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Request Volume</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-blue-200">Requests/Hour</span>
                <span className="text-sm font-bold text-white">{config.requestsPerHour}</span>
              </div>
              <div className="w-full bg-slate-700/30 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full"
                  style={{ width: `${(config.requestsPerHour / 10000) * 100}%` }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-blue-300/70">Average load distribution</p>
          </div>
        </div>

        {/* Health */}
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">System Health</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-200">CPU Usage</span>
              <span className="text-sm font-bold text-white">34%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-200">Memory Usage</span>
              <span className="text-sm font-bold text-white">52%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-200">Error Rate</span>
              <span className="text-sm font-bold text-green-600">0.02%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-200">Queue Depth</span>
              <span className="text-sm font-bold text-white">12</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3 pb-3 border-b border-blue-500/20">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-white">System health check passed</p>
              <p className="text-xs text-blue-300/70">2 minutes ago</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 pb-3 border-b border-blue-500/20">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-white">Processed {config.requestsPerHour} requests</p>
              <p className="text-xs text-blue-300/70">Last hour</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-white">Performance optimal</p>
              <p className="text-xs text-blue-300/70">Continuous monitoring active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
